import { expandBorderWidth, expandPadding, paddingUtil } from "@gadgetinc/themes/src/utils";
import { mergeOverrides, useStyletron } from "baseui";
import { DeleteAlt } from "baseui/icon";
// eslint-disable-next-line no-restricted-imports
import type { InputProps as BaseUIInputProps } from "baseui/input";
// eslint-disable-next-line no-restricted-imports
import { Input as BaseUIInput } from "baseui/input";
import type { Overrides } from "baseui/overrides";
import { Popover } from "baseui/popover";
import React, { RefObject, forwardRef } from "react";
import { RichString } from "state-trees/src/RichString";
import { StyleObject } from "styletron-react";

// eslint-disable-next-line no-restricted-imports
export { SIZE } from "baseui/input";

export const InputErrorWrapper = (props: {
  children: React.ReactNode;
  error?: string;
  $style?: StyleObject;
  parentRef?: RefObject<HTMLElement>;
}) => {
  const [css, $theme] = useStyletron();

  const error = RichString.parseToString(props.error ?? "");

  return (
    <Popover
      isOpen={error !== "" && error !== undefined}
      content={
        <div
          className={css({
            whiteSpace: "normal",
            backgroundColor: $theme.colors.red400,
            ...$theme.typography.caption,
            color: $theme.colors.backgroundPrimary,
            ...paddingUtil($theme.sizing.scale100, $theme.sizing.scale300),
            borderRadius: $theme.borders.radius200,
            minWidth: `${props.parentRef?.current?.offsetWidth}px` ?? "163px",
            maxWidth: `${props.parentRef?.current?.offsetWidth}px` ?? "300px",
            wordWrap: "break-word",
            ...props.$style,
          })}
        >
          {error}
        </div>
      }
      placement="bottomLeft"
      triggerType="click"
    >
      <div className={css({ flexGrow: 1, zIndex: 2, height: "100%" })}>{props.children}</div>
    </Popover>
  );
};

export type InputProps = BaseUIInputProps & {
  dataTestId?: string;
};
/**
 * BaseUI re-export for minimal inputs because we need to pass overrides to style the borders right. Whereever you want to use an <Input /> from baseui, you should use this instead.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const { dataTestId, ...inputProps } = props;
  return (
    <BaseUIInput
      inputRef={ref}
      {...inputProps}
      overrides={mergeOverrides(
        {
          Root: {
            style: ({ $theme, $isFocused }) => {
              return {
                ...expandBorderWidth("1px"),
                ...expandPadding(0),
                ...($isFocused
                  ? {
                      boxShadow: `inset 0 0 0 1px ${$theme.colors.borderFocus}`,
                    }
                  : {}),
              };
            },
          },
          Input: {
            props: {
              "data-testid": dataTestId,
            },
          },
          InputContainer: {
            style: {
              backgroundColor: "transparent",
            },
          },
          StartEnhancer: {
            style: {
              backgroundColor: "transparent",
            },
          },
          EndEnhancer: {
            style: {
              backgroundColor: "transparent",
            },
          },
          ClearIcon: <DeleteAlt size={16} />,
        },
        // TypeScript can't determine that InputOverrides interface is compatible with Overrides<any>
        // See https://github.com/microsoft/TypeScript/issues/15300
        props.overrides as Overrides<any> | undefined
      )}
    />
  );
});
